import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { RuleInterface } from '@vendasta/automata';
import { VaFormsModule } from '@vendasta/forms';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyCheckboxModule } from '@vendasta/galaxy/checkbox';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { TagsService } from '../../../../../data-services/tags.service';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';

@Component({
  selector: 'automata-remove-tag',
  templateUrl: './remove-tag-panel.component.html',
  styleUrls: ['./remove-tag-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    VaFormsModule,
    GalaxyCheckboxModule,
  ],
})
export class RemoveTagPanelComponent extends CommonActionPanelComponent implements OnInit, OnDestroy {
  tagsControl: UntypedFormControl = new UntypedFormControl([]);
  removeAllTagsControl: UntypedFormControl = new UntypedFormControl();
  form: UntypedFormGroup;
  options$: Observable<{ name: string; value: string }[]>;
  destroyed$$ = new Subject<void>();
  loadingTagList: boolean;

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly fb: UntypedFormBuilder,
    private readonly tagService: TagsService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    // Create the form, populating the controls with any stored data.
    // Ensure it performs validation on the entire form and not just on a single control.
    this.tagsControl.setValue(jsonData.tags || []);
    this.removeAllTagsControl.setValue(jsonData.remove_all_tags);
    this.form = this.fb.group(
      {
        tags: this.tagsControl,
        removeAll: this.removeAllTagsControl,
      },
      { validators: this.isInputValid },
    );

    // Get the tag service ready.
    this.loadingTagList = true;
    this.options$ = this.tagService.tags$.pipe(
      map((tags) => {
        return tags.map((t) => ({
          name: t,
          value: t,
        }));
      }),
      tap((_) => (this.loadingTagList = false)),

      // Once the list of tags is ready saving is possible (providing that valid input is given).
      tap(() => (this.savingEnabled = true)),
    );
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
  }

  /**
   * Returns true if the form has valid input, i.e. tags are provided or
   * remove all is true.
   * @private
   */
  private isInputValid: ValidatorFn = (_control: AbstractControl): ValidationErrors | null => {
    // We either need tags or the checkbox to be ticked.
    if (!(this.tagsControl.value.length > 0 || !!this.removeAllTagsControl.value)) {
      return { noTagsToRemove: true };
    }

    return null;
  };

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    if (!this.form) {
      return jsonData;
    }
    jsonData.remove_all_tags = !!this.removeAllTagsControl.value;
    if (jsonData.remove_all_tags) {
      jsonData.tags = [];
    } else {
      jsonData.tags = this.tagsControl.value;
    }
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  /**
   * @see isInputValid
   */
  isFormValid(): boolean {
    return this.form.valid;
  }
}
